import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Wallet, Cpu, TrendingUp, Check, History, ExternalLink, Rocket } from "lucide-react";
import { toast } from "sonner";
import { ethers } from "ethers";
import SectionLabel from "@/components/SectionLabel";
import { useWallet } from "@/context/WalletContext";
import { config } from "@/config";
import { getSettings, submitDeposit, getDepositHistory } from "@/lib/api";
import { depositUsdt } from "@/lib/deposit";
import { notifySuccess } from "@/lib/notify";

const QUICK = [1, 10, 50, 100];
const ERC20_ABI = ["function balanceOf(address) view returns (uint256)"];
const money = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const whole = (n) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

export default function StakePage() {
  const { address, setUser } = useWallet();
  const [amount, setAmount] = useState("10");
  const [agreed, setAgreed] = useState(false);
  const [walletUsdt, setWalletUsdt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manualHash, setManualHash] = useState("");

  const minDep = settings?.min_deposit_usd || 10;
  const val = Math.max(0, Number(amount) || 0);
  const cap = val * 2;
  const daily = cap * 0.005;

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);

  const loadHistory = useCallback(async () => {
    if (!address) return;
    try { const h = await getDepositHistory(address); setDeposits(h.rows || []); } catch (e) { /* ignore */ }
  }, [address]);

  const loadBalance = useCallback(async () => {
    if (!address || !settings?.usdt_contract) { setWalletUsdt(null); return; }
    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const usdt = new ethers.Contract(settings.usdt_contract, ERC20_ABI, provider);
      const bal = await usdt.balanceOf(address);
      setWalletUsdt(Number(ethers.formatUnits(bal, 18)));
    } catch (e) { setWalletUsdt(null); }
  }, [address, settings]);

  useEffect(() => { loadBalance(); loadHistory(); }, [loadBalance, loadHistory]);

  const finish = (res) => {
    if (res?.user) setUser(res.user);
    notifySuccess("Deposit Confirmed", `$${res?.amount_usd ?? val} USDT credited · mining cap 2x active`);
    setAgreed(false);
    loadBalance();
  };

  const deposit = async () => {
    if (!address) return toast.error("Connect your wallet first");
    if (val < minDep) return toast.error(`Minimum deposit is $${minDep}`);
    if (!agreed) return toast.error("Please agree to the deposit terms first");
    if (!settings?.admin_wallet) return toast.error("Deposit address not loaded, please retry");
    if (walletUsdt !== null && walletUsdt < val) {
      return toast.error(`Insufficient USDT. You have ${money(walletUsdt)}, need $${val}`);
    }
    setBusy(true);
    try {
      setStep("Confirm USDT transfer in your wallet…");
      const txHash = await depositUsdt(val, settings.admin_wallet, settings.usdt_contract);
      setStep("Verifying deposit on-chain…");
      const res = await submitDeposit({ address, tx_hash: txHash });
      finish(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.shortMessage || e?.reason || e?.message || "Deposit failed";
      toast.error(msg.includes("missing revert") ? "Wallet not on BSC or has no USDT/BNB. Check network & balance." : msg);
    } finally { setBusy(false); setStep(""); }
  };

  const submitManual = async () => {
    const h = manualHash.trim();
    if (!/^0x[0-9a-fA-F]{64}$/.test(h)) return toast.error("Enter a valid transaction hash");
    setBusy(true);
    try {
      setStep("Verifying your transaction…");
      const res = await submitDeposit({ address, tx_hash: h });
      setManualHash("");
      finish(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Verification failed");
    } finally { setBusy(false); setStep(""); }
  };

  return (
    <div data-testid="stake-page" className="flex flex-col gap-4 px-4 pt-4 pb-4">
      <SectionLabel center>Deposit USDT</SectionLabel>

      {/* Wallet balance */}
      <div data-testid="stake-wallet-card" className="card-glow flex items-center justify-between p-4">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#34D07A]">
          <Wallet size={16} className="text-[#34D07A]" /> Your Wallet USDT
        </span>
        <span data-testid="stake-wallet-balance" className="text-lg font-bold text-[#34D07A]">
          {walletUsdt === null ? "—" : `$${money(walletUsdt)}`}
        </span>
      </div>

      {/* Mining cap + daily generation */}
      <div className="grid grid-cols-2 gap-3">
        <div data-testid="mining-cap-granted" className="card-glow p-4">
          <Cpu size={16} className="mb-2 text-[#34D07A]" />
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[#34D07A]">Mining Cap (2×)</div>
          <div data-testid="mining-cap-value" className="text-xl font-extrabold text-[#34D07A]">${whole(cap)}</div>
          <div className="text-[11px] text-white/50">200% of your deposit</div>
        </div>
        <div data-testid="daily-generation" className="card-glow p-4">
          <TrendingUp size={16} className="mb-2 text-[#34D07A]" />
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[#34D07A]">Daily ROI</div>
          <div data-testid="daily-gen-value" className="text-xl font-extrabold text-[#34D07A]">${money(daily)}<span className="text-xs text-white/40">/day</span></div>
          <div className="text-[11px] text-white/50">1% / day</div>
        </div>
      </div>

      {/* Amount input */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-glow p-5">
        <div className="mb-1 flex items-center gap-2">
          <Rocket size={16} className="text-[#34D07A]" />
          <span className="text-sm font-bold grad-title">Deposit USDT &amp; Activate</span>
        </div>
        <p className="mb-4 text-[11px] text-white/45">
          Activate with as little as <b className="text-[#D6C51E]">${minDep}</b> and start earning up to <b className="text-[#34D07A]">2×</b>. Credited automatically.
        </p>
        <div className="mb-4 flex items-center rounded-xl border border-[#3C6B33]/50 bg-black/30 px-4">
          <span className="text-sm text-white/40">$</span>
          <input data-testid="stake-amount-input" type="number" inputMode="numeric" min={minDep} value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent py-3 pl-1 text-lg font-bold text-[#34D07A] outline-none placeholder:text-white/30" placeholder="10" />
          <span className="text-xs font-semibold text-white/40">USDT</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {QUICK.map((q) => (
            <button key={q} data-testid={`stake-quick-${q}`} onClick={() => setAmount(String(q))}
              className={`rounded-xl border py-2.5 text-sm font-bold transition-all active:scale-95 ${Number(amount) === q ? "border-[#0AA84F] bg-[#0AA84F]/15 text-[#34D07A] shadow-[0_0_12px_rgba(10,168,79,0.35)]" : "border-[#3C6B33]/50 bg-white/5 text-white/60"}`}>
              ${q}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Terms agreement */}
      <button data-testid="stake-terms" onClick={() => setAgreed((v) => !v)} className="card-glow flex items-center gap-3 p-4 text-left">
        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all ${agreed ? "border-[#0AA84F] bg-[#0AA84F]" : "border-[#3C6B33]/70 bg-transparent"}`}>
          {agreed && <Check size={14} className="text-black" />}
        </span>
        <span className="text-xs text-white/70">I agree to the staking and platform terms.</span>
      </button>

      <button data-testid="stake-submit-btn" onClick={deposit} disabled={!agreed || val < minDep || busy}
        className="mt-1 h-12 w-full rounded-xl bg-gradient-to-r from-[#0AA84F] via-[#65B82E] to-[#D6C51E] text-base font-bold text-black transition-all active:scale-[0.98] shadow-[0_0_18px_rgba(10,168,79,0.45)] disabled:opacity-40 disabled:shadow-none">
        {busy ? (step || "Processing…") : `Deposit $${whole(val)} USDT`}
      </button>

      <div data-testid="manual-resubmit" className="mt-2 rounded-xl border border-[#3C6B33]/40 bg-black/20 p-3">
        <p className="mb-2 text-[11px] leading-relaxed text-white/55">
          Already sent USDT but it didn't show as staked? Paste the BscScan transaction hash to verify &amp; credit it.
        </p>
        <div className="flex items-center gap-2">
          <input
            data-testid="manual-tx-input"
            value={manualHash}
            onChange={(e) => setManualHash(e.target.value)}
            placeholder="0x… transaction hash"
            className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 text-xs font-mono text-white outline-none placeholder:text-white/30"
          />
          <button
            data-testid="manual-submit-btn"
            disabled={busy || !manualHash.trim()}
            onClick={submitManual}
            className="shrink-0 rounded-lg border border-[#34D07A]/50 bg-[#0AA84F]/15 px-4 py-2.5 text-xs font-bold text-[#34D07A] active:scale-95 disabled:opacity-40"
          >
            Verify
          </button>
        </div>
      </div>

      {/* Recent staking (deposits) */}
      <div data-testid="stake-history" className="card-glow mt-2 p-5">
        <div className="mb-1 flex items-center gap-2">
          <History size={16} className="text-[#34D07A]" />
          <span className="text-base font-bold grad-title">Your Staking</span>
        </div>
        {deposits.length === 0 ? (
          <div className="border-t border-white/5 py-6 text-center text-xs text-white/45">No staking yet.</div>
        ) : (
          deposits.slice(0, 8).map((dpt) => {
            const isCompound = dpt.kind === "compound" || !dpt.tx_hash;
            const rowKey = dpt.tx_hash || dpt.id || dpt.created_at;
            return (
            <div key={rowKey} data-testid={`stake-row-${rowKey}`} className="flex items-center justify-between gap-2 border-t border-white/5 py-2.5 text-xs">
              <span className="text-white/55">{(dpt.created_at || "").slice(0, 10)}</span>
              <span className="font-semibold text-[#34D07A]">${money(dpt.amount_usd)}</span>
              {isCompound ? (
                <span data-testid={`stake-compound-${rowKey}`} className="rounded-lg border border-[#D6C51E]/40 px-2 py-1 text-[10px] font-semibold text-[#D6C51E]">
                  Compounding
                </span>
              ) : (
                <a
                  data-testid={`stake-tx-${dpt.tx_hash}`}
                  href={`${config.explorer}/tx/${dpt.tx_hash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 rounded-lg border border-[#34D07A]/40 px-2 py-1 font-mono text-[10px] font-semibold text-[#34D07A] active:scale-95"
                >
                  {dpt.tx_hash.slice(0, 6)}…{dpt.tx_hash.slice(-4)} <ExternalLink size={12} />
                </a>
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
